// ─── Modifier Keys ────────────────────────────────────────────────────────────

export type ModifierKey = 'ctrl' | 'meta' | 'alt' | 'shift';

// ─── Scope Types ──────────────────────────────────────────────────────────────

/**
 * Scope hierarchy for shortcut priority resolution.
 *
 * When multiple handlers match the same key combo, the highest-priority
 * scope wins. Global shortcuts (scope === 'global') always fire regardless
 * of other active scopes — they are never blocked.
 *
 * Priority ladder (highest wins):
 *   field   50  — inside an <input>, <textarea>, or <select>
 *   dialog  40  — inside an open dialog / modal
 *   form    30  — inside a form component
 *   table   30  — inside a table component (same level as form)
 *   page    20  — current route page
 *   module  10  — active ERP module
 *   global   0  — always fires, never blocked
 */
export type ShortcutScope = 'global' | 'module' | 'page' | 'form' | 'table' | 'dialog' | 'field';

export const SCOPE_PRIORITY = {
  global: 0,
  module: 10,
  page: 20,
  form: 30,
  table: 30,
  dialog: 40,
  field: 50,
} as const satisfies Record<ShortcutScope, number>;

// ─── Key Combo ────────────────────────────────────────────────────────────────

export interface ShortcutCombo {
  /**
   * Key value as per `KeyboardEvent.key` (case-insensitive).
   * Examples: 'k', 'Enter', 'Escape', 'F1', 'ArrowDown', 's', '/'
   */
  key: string;
  /** Required modifier keys. 'ctrl' matches both Ctrl and Cmd on macOS. */
  modifiers?: ModifierKey[];
}

// ─── Help Metadata ────────────────────────────────────────────────────────────

export interface ShortcutMeta {
  /** Human-readable label shown in shortcut help overlay. e.g. "Save form" */
  label: string;
  /** Grouping label for help overlay. e.g. "Form actions" */
  group?: string;
  /** Whether to show in help overlay. Default: true */
  visible?: boolean;
}

// ─── Internal Definition ──────────────────────────────────────────────────────

/**
 * Full internal shortcut definition stored in the registry.
 * Created by `ShortcutRegistry.register()`.
 */
export interface ShortcutDefinition {
  readonly id: string;
  readonly combo: Readonly<ShortcutCombo>;
  readonly scope: ShortcutScope;
  /**
   * Scope instance ID. When set, this shortcut only fires when this exact
   * scope ID has been activated via `registry.activateScope(scopeId)`.
   *
   * Use this for precise isolation — e.g. a dialog's Esc handler should only
   * fire when THAT dialog is the topmost open one.
   *
   * When omitted, the shortcut is eligible whenever it is registered (i.e.
   * the component is mounted). React's lifecycle naturally handles this.
   */
  readonly scopeId?: string;
  /**
   * Effective numeric priority. Defaults to SCOPE_PRIORITY[scope].
   * Override for fine-grained control within the same scope level.
   */
  readonly priority: number;
  readonly handler: (event: KeyboardEvent) => void;
  readonly preventDefault: boolean;
  readonly stopPropagation: boolean;
  /** Mutable — changed by enable() / disable() without re-registration. */
  enabled: boolean;
  readonly meta?: Readonly<ShortcutMeta>;
  /** Internal: registration order. Higher = more recently registered (LIFO tiebreaker). */
  readonly seq: number;
}

// ─── Input for Registration ───────────────────────────────────────────────────

/**
 * Input accepted by `ShortcutRegistry.register()` and `useRegisterShortcut()`.
 * All fields except `combo`, `scope`, and `handler` are optional.
 */
export interface ShortcutInput {
  /** Override auto-generated ID. Stable IDs are useful for enable/disable. */
  id?: string;
  combo: ShortcutCombo;
  scope: ShortcutScope;
  scopeId?: string;
  priority?: number;
  handler: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  enabled?: boolean;
  meta?: ShortcutMeta;
}

// ─── Change Events ────────────────────────────────────────────────────────────

export type ShortcutChangeType =
  | 'registered'
  | 'unregistered'
  | 'enabled'
  | 'disabled'
  | 'scope:activated'
  | 'scope:deactivated';

export interface ShortcutChangeEvent {
  type: ShortcutChangeType;
  id?: string;
  scopeId?: string;
}

export type ChangeListener = (event: ShortcutChangeEvent) => void;

// ─── Help overlay grouping ────────────────────────────────────────────────────

export interface ShortcutHelpGroup {
  group: string;
  shortcuts: readonly ShortcutDefinition[];
}
